/**
 * AchievementToaster — Xbox-style bottom-right achievement toaster
 * rendered in Clash of Clans / Supercell visual language.
 *
 * Each toast:
 *   - Slides in from below the bottom-right corner
 *   - Shows an ornate gold-trimmed carved-stone frame
 *   - Displays the achievement icon, "ACHIEVEMENT UNLOCKED!" header,
 *     name, and description
 *   - Plays a fanfare SFX
 *   - Subtle shimmer sweep across the frame
 *   - Holds for ~3.5s, then slides back down
 *
 * Usage:
 *   AchievementToaster.get(scene).show({
 *     icon: '🏆',
 *     name: 'Ocean Savior',
 *     description: 'Defeat the Pollution Hydra',
 *   });
 *
 * Multiple toasts queue automatically.
 */

import Phaser from 'phaser';
import { AudioManager } from './AudioManager';

export type ToastTier = 'common' | 'rare' | 'epic' | 'legendary';

export interface ToastData {
  icon: string;        // Emoji or unicode character
  name: string;        // Achievement name
  description: string; // Short description
  tier?: ToastTier;    // Optional tier — drives fanfare SFX selection. Defaults to 'rare'.
}

export class AchievementToaster {
  private static instances = new WeakMap<Phaser.Scene, AchievementToaster>();

  static get(scene: Phaser.Scene): AchievementToaster {
    let inst = this.instances.get(scene);
    if (!inst) {
      inst = new AchievementToaster(scene);
      this.instances.set(scene, inst);
      scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        this.instances.delete(scene);
      });
    }
    return inst;
  }

  private scene: Phaser.Scene;
  private queue: ToastData[] = [];
  private isShowing = false;

  private constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  show(data: ToastData): void {
    this.queue.push(data);
    if (!this.isShowing) this.showNext();
  }

  private showNext(): void {
    if (this.queue.length === 0) {
      this.isShowing = false;
      return;
    }
    this.isShowing = true;
    const data = this.queue.shift()!;
    this.render(data);
  }

  private render(data: ToastData): void {
    const scene = this.scene;
    if (!scene || !scene.sys?.isActive()) {
      this.showNext();
      return;
    }

    const { width, height } = scene.cameras.main;

    // Toast dimensions — positioned so it sits clearly within the screen
    // above the HUD and to the right
    const toastW = 460;
    const toastH = 140;
    const marginRight = 40;
    const marginBottom = 60;
    const targetX = width - toastW - marginRight;
    const targetY = height - toastH - marginBottom;
    const startY = height + 50; // Start below screen

    // Container for the whole toast (so we can tween it as one unit)
    const container = scene.add.container(targetX + toastW / 2, startY + toastH / 2);
    container.setDepth(20000);
    container.setScrollFactor(0);

    // ─── Carved stone backdrop with gold ornate border ─────────────────
    // Draw the backdrop in a single graphics for crisp edges
    const bgGfx = scene.add.graphics();
    // Outer dark shadow
    bgGfx.fillStyle(0x000000, 0.5);
    bgGfx.fillRoundedRect(-toastW / 2 + 4, -toastH / 2 + 4, toastW, toastH, 14);
    // Main stone backdrop (dark blue-grey)
    bgGfx.fillStyle(0x1a2a3e, 0.95);
    bgGfx.fillRoundedRect(-toastW / 2, -toastH / 2, toastW, toastH, 14);
    // Inner highlight band
    bgGfx.fillStyle(0x243d58, 0.8);
    bgGfx.fillRoundedRect(-toastW / 2 + 6, -toastH / 2 + 6, toastW - 12, toastH - 12, 10);

    // ─── Ornate gold frame (thick gold border + corner gems) ──────────
    bgGfx.lineStyle(4, 0xFFD700, 1);
    bgGfx.strokeRoundedRect(-toastW / 2, -toastH / 2, toastW, toastH, 14);
    bgGfx.lineStyle(1, 0xFFE880, 0.8);
    bgGfx.strokeRoundedRect(-toastW / 2 + 2, -toastH / 2 + 2, toastW - 4, toastH - 4, 12);

    container.add(bgGfx);

    // Corner gem decorations (blue sapphires)
    const gemOffsets = [
      { x: -toastW / 2 + 14, y: -toastH / 2 + 14 },
      { x:  toastW / 2 - 14, y: -toastH / 2 + 14 },
      { x: -toastW / 2 + 14, y:  toastH / 2 - 14 },
      { x:  toastW / 2 - 14, y:  toastH / 2 - 14 },
    ];
    for (const g of gemOffsets) {
      const gem = scene.add.circle(g.x, g.y, 5, 0x4dd0e1, 1);
      gem.setStrokeStyle(1.5, 0xFFD700, 1);
      container.add(gem);

      // Inner bright highlight on gem
      const hi = scene.add.circle(g.x - 1.5, g.y - 1.5, 1.5, 0xFFFFFF, 0.9);
      container.add(hi);
    }

    // ─── Icon panel on the left (circle with glow) ─────────────────────
    const iconPanelX = -toastW / 2 + 60;
    const iconPanelY = 0;

    // Radial glow behind icon
    const iconGlow = scene.add.circle(iconPanelX, iconPanelY, 46, 0xFFD700, 0.3);
    iconGlow.setBlendMode(Phaser.BlendModes.ADD);
    container.add(iconGlow);

    // Icon circle backing
    const iconBg = scene.add.circle(iconPanelX, iconPanelY, 38, 0x0a1628, 1);
    iconBg.setStrokeStyle(3, 0xFFD700, 1);
    container.add(iconBg);
    const iconBgInner = scene.add.circle(iconPanelX, iconPanelY, 34, 0x243d58, 0.8);
    container.add(iconBgInner);

    // Icon emoji (big)
    const iconText = scene.add.text(iconPanelX, iconPanelY, data.icon, {
      fontSize: '52px',
    });
    iconText.setOrigin(0.5);
    container.add(iconText);

    // Pulse the glow
    scene.tweens.add({
      targets: iconGlow,
      scale: { from: 1, to: 1.15 },
      alpha: { from: 0.3, to: 0.5 },
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // ─── Text content on the right ─────────────────────────────────────
    const textX = -toastW / 2 + 130;

    // Header "ACHIEVEMENT UNLOCKED!"
    const header = scene.add.text(textX, -28, 'ACHIEVEMENT UNLOCKED!', {
      fontSize: '20px',
      fontFamily: 'CF Asty Std',
      color: '#FFD700',
      stroke: '#000000',
      strokeThickness: 3,
      shadow: { offsetX: 0, offsetY: 2, color: '#000000', blur: 3, fill: true },
    });
    header.setOrigin(0, 0.5);
    container.add(header);

    // Achievement name (big)
    const name = scene.add.text(textX, 0, data.name, {
      fontSize: '34px',
      fontFamily: 'CF Asty Std',
      color: '#FFFFFF',
      stroke: '#000000',
      strokeThickness: 4,
      shadow: { offsetX: 0, offsetY: 3, color: '#000000', blur: 5, fill: true },
    });
    name.setOrigin(0, 0.5);
    container.add(name);

    // Description
    const desc = scene.add.text(textX, 30, data.description, {
      fontSize: '16px',
      fontFamily: 'CF Asty Std',
      color: '#9ee4f2',
      stroke: '#001a33',
      strokeThickness: 2,
    });
    desc.setOrigin(0, 0.5);
    container.add(desc);

    // ─── Gold shimmer sweep overlay (light reflection across the frame) ─
    const shimmer = scene.add.graphics();
    shimmer.fillStyle(0xFFFFFF, 0.2);
    shimmer.fillRect(-toastW / 2 - 40, -toastH / 2, 30, toastH);
    shimmer.setBlendMode(Phaser.BlendModes.ADD);
    // Mask the shimmer to the toast bounds using a geometry mask
    container.add(shimmer);

    // ─── Play tiered fanfare SFX ──────────────────────────────────────
    // Masterplan §7: common → single chime, rare → 3 notes,
    // epic → 5-note brass, legendary → full horn stinger.
    // Each tier falls back to the next lower tier if the v2 cue is missing,
    // then to legacy cues.
    try {
      const audio = AudioManager.getInstance();
      const requested = data.tier || 'rare'; // default to rare if unset
      const tierPriority: Record<ToastTier, string[]> = {
        legendary: ['sfx_fanfare_legendary', 'sfx_fanfare_epic', 'sfx_fanfare_rare', 'sfx_fanfare_common', 'stinger_perfect', 'sfx_star_earn'],
        epic: ['sfx_fanfare_epic', 'sfx_fanfare_rare', 'sfx_fanfare_common', 'stinger_perfect', 'sfx_star_earn'],
        rare: ['sfx_fanfare_rare', 'sfx_fanfare_common', 'stinger_perfect', 'sfx_star_earn'],
        common: ['sfx_fanfare_common', 'sfx_fanfare_rare', 'sfx_star_earn'],
      };
      const picked = tierPriority[requested].find((k) => scene.cache.audio.exists(k));
      if (picked) {
        const volumeByTier: Record<ToastTier, number> = {
          legendary: 0.85, epic: 0.75, rare: 0.7, common: 0.55,
        };
        audio.playSFX(scene, picked, { volume: volumeByTier[requested] });
      }
    } catch {}

    // ─── Entrance animation (slide up + bounce) ────────────────────────
    scene.tweens.add({
      targets: container,
      y: targetY + toastH / 2,
      duration: 500,
      ease: 'Back.easeOut',
      easeParams: [2],
    });

    // Icon bounce
    scene.tweens.add({
      targets: iconText,
      scale: { from: 0, to: 1 },
      duration: 600,
      delay: 200,
      ease: 'Back.easeOut',
      easeParams: [3],
    });

    // Shimmer sweep (delayed)
    scene.time.delayedCall(700, () => {
      scene.tweens.add({
        targets: shimmer,
        x: toastW + 80,
        duration: 800,
        ease: 'Sine.easeInOut',
      });
    });

    // ─── Exit animation after 3.5s ─────────────────────────────────────
    scene.time.delayedCall(3500, () => {
      scene.tweens.add({
        targets: container,
        y: startY + toastH / 2,
        alpha: 0.6,
        duration: 450,
        ease: 'Power2',
        onComplete: () => {
          container.destroy();
          this.showNext();
        },
      });
    });
  }
}
